/**
 * Functions related to created an a11y-compliant multi-select. This is intended to be used as a substitute for calling
 * GCHomeCommon.loadDropDown() for a <select multiple></select> component (i.e. a native multi-select).
 *
 * USAGE:
 * Create a div container:
 *      <div id="listOpportunities"></div>
 * Make javascript call to load the data and initialize the dropdown:
 *      A11yMultiSelect.loadMultiSelect(baseURL + '/AssignCareStaff/CareOpportunities/', 'listOpportunities', 'OppId', 'OppName', memOpportunity);
 *
 * NOTE:
 * I used the following article from A360 to implement this component: https://hub.accessible360.com/kb/articles/438#heading-multiselectable-listboxes
 */
const A11yMultiSelect = (function () {

    let _LOG_COLOR = 'violet';

    return {
        LOG_COLOR: _LOG_COLOR,

        getSelectedOptions: _getSelectedOptions,
        loadMultiSelect: _loadMultiSelect,
        deselectAllOptions: _deselectAllOptions
    };

    /**
     * Loads the data and constructs the multi-select dropdown component. Sets up listeners for various a11y-required events.
     * @param {any} url - API endpoint to load the dropdown data
     * @param {any} elementId - ID of the div container where the multi-select will be loaded
     * @param {any} dataValue - the name of the field to source the underlying ID of each option in the list
     * @param {any} dataText - the name of the field to source the display value of each option in the list
     * @param {any} preselectedOptionIds - array of IDs that should be preselected after the multi-select is constructed
     */
    function _loadMultiSelect(url, elementId, dataValue, dataText, preselectedOptionIds) {
        // make call to legacy GCHomeCommon.loadDropDown() to get the dropdown data...
        return loadDropDown(url)
            .then(function (data) {
                // then construct the a11y-compliant multi-select dropdown...
                _convertNativeMultiSelect(elementId, dataValue, dataText, data);
                if (preselectedOptionIds) {
                    _presetSelectedOptions(elementId, preselectedOptionIds);
                }
            });
    }

    /**
     * Returns an array of selected option IDs in the multi-select (call this to get your selected options when submitting a form)
     * @param {any} elementId - ID of the div container where the multi-select is loaded
     */
    function _getSelectedOptions(elementId) {
        let selectedOptionsStr;
        if (document.getElementById(elementId + '_hidden')) {
            selectedOptionsStr = document.getElementById(elementId + '_hidden').value;
        }
        return selectedOptionsStr && selectedOptionsStr !== '' ? selectedOptionsStr.split(',') : [];
    }
    /**
* Clears all selected options in the multi-select
* @param {any} elementId - ID of the div container where the multi-select is loaded
*/
    function _deselectAllOptions(elementId) {
        let container = document.getElementById(elementId + '_container');
        let hiddenField = container.querySelector('.ah-multiselect-hidden-field');
        let options = container.querySelectorAll('.ah-multiselect-options li');

        options.forEach(function (option) {
            option.setAttribute('aria-selected', false);
        });
        hiddenField.value = '';
    }
    ///////////////////////
    // private functions //
    ///////////////////////
    function _convertNativeMultiSelect(elementId, dataValue, dataText, data) {
        _logDebug('convertNativeMultiSelect(): converting native multi-select to a11y-compliant multi-select [' + elementId + ']', { dataValue: dataValue, dataText: dataText, data: data });

        let html = '<div id="' + elementId + '_container" class="ah-multiselect">';
        html += '<input type="hidden" id="' + elementId + '_hidden" name="selected-option" class="ah-multiselect-hidden-field" value="" style="width:99%;">';
        html += '<ul class="ah-multiselect-options" role="listbox" tabindex="0" aria-multiselectable="true" aria-labelledby="' + _getAssociatedLabelId(elementId) + '">';

        if (data && data.length > 0) {
            data.forEach(function (option, index) {
                html += '<li role="option" id="' + option[dataValue] + '" aria-selected="false" data-index="' + index + '" unselectable="on">' + option[dataText] + '</li>';
            });
        }

        html += '</ul>';
        html += '</div>';

        $('#' + elementId).html('').append(html);

        let container = document.getElementById(elementId + '_container');
        let hiddenField = container.querySelector('.ah-multiselect-hidden-field');
        let optionsList = container.querySelector('.ah-multiselect-options');
        let options = optionsList.querySelectorAll('li');
        let selectedOptions = [];
        let lastClickedOption = null;

        // setup event listeners
        $(optionsList).keydown(function (e) {
            handleOptionsListKeydown(e);
        });

        $(optionsList).keyup(function (e) {
            handleOptionsListKeyup(e);
        });

        $(options).each(function () {
            $(this).click(function (e) {
                if (e.shiftKey) {
                    handleShiftClick(e);
                } else {
                    handleOptionClick(e);
                }
            });

            // prevent the ability for the user to select the <li> text (otherwise weird stuff happens on Shift/Ctrl-click)
            $(this).on('selectstart', false);
        });

        $(document).click(function (e) {
            handleBodyClick(e);
        });

        // event handlers
        function handleBodyClick(e) {
            if (!container.contains(e.target)) {
                _hideContainerFocus(container);
                _removeHighlight(options);
            }
        }

        function handleOptionClick(e) {
            var shouldBeSelected = e.target.getAttribute('aria-selected') === 'true' ? false : true;
            _removeHighlight(options);
            _highlightOption(optionsList, e.target);

            if (shouldBeSelected) {
                _selectOption(e.target, selectedOptions, hiddenField);
                lastClickedOption = e.target;
            } else {
                _deselectOption(e.target, selectedOptions, hiddenField);
            }

            _showContainerFocus(container);
        }

        // when user shift-clicks on a list option, select everything in between
        function handleShiftClick(e) {
            if (lastClickedOption) {
                var shouldBeSelected = e.target.getAttribute('aria-selected') === 'true' ? false : true;

                let lastClickedOptionIndex = lastClickedOption.getAttribute('data-index');
                let currentClickedOptionIndex = e.target.getAttribute('data-index');
                let lowIndex = lastClickedOptionIndex;
                let highIndex = currentClickedOptionIndex;

                if (lastClickedOptionIndex > currentClickedOptionIndex) {
                    lowIndex = currentClickedOptionIndex;
                    highIndex = lastClickedOptionIndex;
                }

                for (var i = lowIndex; i <= highIndex; i++) {
                    if (shouldBeSelected) {
                        _selectOption(options[i], selectedOptions, hiddenField);
                    } else {
                        _deselectOption(options[i], selectedOptions, hiddenField);
                    }
                }

                _removeHighlight(options);
                _highlightOption(optionsList, e.target);
                lastClickedOption = e.target;
            }
        }

        function handleOptionsListKeydown(e) {
            switch (e.key) {
                case 'ArrowUp':
                    e.preventDefault();
                    _highlightPreviousOption(optionsList, options);
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    _highlightNextOption(optionsList, options);
                    break;
                case 'Home':
                    e.preventDefault();
                    _removeHighlight(options);
                    _highlightFirstOption(optionsList, options);
                    break;
                case 'End':
                    e.preventDefault();
                    _removeHighlight(options);
                    _highlightLastOption(optionsList, options);
                    break;
                case 'Tab':
                    _hideContainerFocus(container);
                    _removeHighlight(options);
                    break;
                case ' ':   // spacebar
                    e.preventDefault();
                    _toggleHighlightedOption(optionsList, selectedOptions, hiddenField);
                    break;
                default:
                // do nothing (this is here for sonar)
            }
        }

        function handleOptionsListKeyup(e) {
            if (e.key === "Tab" && document.activeElement === e.target) {
                _showContainerFocus(container);
                _highlightFirstOption(optionsList, options);
            }
        }
    }

    // find and return the multiselect's associated form label
    function _getAssociatedLabelId(elementId) {
        let associatedLabelId = null;
        let associatedLabelElem = document.querySelector('label[for="' + elementId + '"]');

        if (!associatedLabelElem) {
            DevUtils.logWarn('_getAssociatedLabelId(): associated label element not found; add a <label for="' + elementId + '"> element to the screen', 'A11yMultiSelect');
            associatedLabelId = '';
        } else {
            associatedLabelId = associatedLabelElem.getAttribute('id');
            if (!associatedLabelId || associatedLabelId === '') {
                // if a label elem was found but there is no ID attr, add one
                associatedLabelId = elementId + '_label';
                associatedLabelElem.setAttribute('id', associatedLabelId);
            }
        }

        return associatedLabelId;
    }

    /* Highlighting Functions */
    // Add special class for visual highlight, and point to this option on the option list's aria-activedescendant attribute
    function _highlightOption(optionsList, option) {
        option.classList.add('ah-multiselect-is-highlighted');
        optionsList.setAttribute('aria-activedescendant', option.getAttribute('id'));
    }

    function _highlightFirstOption(optionsList, options) {
        var option = options[0];
        _highlightOption(optionsList, option);

        // scroll to top
        optionsList.parentElement.scrollTop = 0;
    }

    function _highlightLastOption(optionsList, options) {
        var option = options[options.length - 1];
        _highlightOption(optionsList, option);

        // scroll to bottom
        let scrollDiv = optionsList.parentElement;
        scrollDiv.scrollTop = scrollDiv.scrollHeight;
    }

    function _highlightNextOption(optionsList, options) {
        var highlightedOption = optionsList.querySelector('.ah-multiselect-is-highlighted');

        _removeHighlight(options);

        if (highlightedOption != null) {
            var highlightedOptionIndex = Array.prototype.slice.call(options).indexOf(highlightedOption);

            if (highlightedOptionIndex < options.length - 1) {
                _highlightOption(optionsList, options[highlightedOptionIndex + 1]);
            }

            highlightedOption.scrollIntoView(true, { behavior: 'smooth', block: 'nearest' });
        }
    }

    function _highlightPreviousOption(optionsList, options) {
        var highlightedOption = optionsList.querySelector('.ah-multiselect-is-highlighted');

        _removeHighlight(options);

        if (highlightedOption != null) {
            var highlightedOptionIndex = Array.prototype.slice.call(options).indexOf(highlightedOption);

            if (highlightedOptionIndex > 0) {
                _highlightOption(optionsList, options[highlightedOptionIndex - 1]);
            }

            highlightedOption.scrollIntoView(false, { behavior: 'smooth', block: 'nearest' });
        }
    }

    function _removeHighlight(options) {
        options.forEach(function (option) {
            option.classList.remove('ah-multiselect-is-highlighted');
        });
    }

    /* functions to add/remove focus ring around the entire multi-select container */
    function _hideContainerFocus(container) {
        container.classList.remove('ah-multiselect-has-focus');
    }

    function _showContainerFocus(container) {
        container.classList.add('ah-multiselect-has-focus');
    }

    /* Functions to handle option selection */
    function _presetSelectedOptions(elementId, selectedOptionIds) {
        _logDebug('presetSelectedOptions(): setting selected options for elementId [' + elementId + ']', { selectedOptionIds: selectedOptionIds });

        if (selectedOptionIds && selectedOptionIds.length > 0) {
            let container = document.getElementById(elementId + '_container');
            let hiddenField = container.querySelector('.ah-multiselect-hidden-field');
            let optionsList = container.querySelector('.ah-multiselect-options');

            selectedOptionIds.forEach(function (selectedOptionId) {
                _selectOption(_getOptionElementById(optionsList, selectedOptionId), _getSelectedOptions(elementId), hiddenField);
            });
        }
    }

    // Sets aria-selected="true" on an option, and captures + displays it in a list of all selected options
    function _selectOption(option, selectedOptions, hiddenField) {
        if (option) {
            let optionId = option.getAttribute('id');
            option.setAttribute("aria-selected", true);

            if (!selectedOptions.includes(optionId)) {
                selectedOptions.push(optionId);
            }

            hiddenField.value = selectedOptions;
        } else {
            DevUtils.logWarn('_selectOption(): option is null, cannot set option to selected', 'A11yMultiSelect');
        }
    }

    // Sets aria-selected="false" on an option, and removes it from the list of selected options
    function _deselectOption(option, selectedOptions, hiddenField) {
        if (option) {
            let optionId = option.getAttribute('id');
            option.setAttribute('aria-selected', false);

            if (selectedOptions.includes(optionId)) {
                selectedOptions.splice(selectedOptions.indexOf(optionId), 1);
            }

            hiddenField.value = selectedOptions;
        } else {
            DevUtils.logWarn('_deselectOption(): option is null, cannot set option to deselected', 'A11yMultiSelect');
        }
    }

    // return the <li> option element where id=optionId
    function _getOptionElementById(options, optionId) {
        let option;

        if (options) {
            option = options.querySelector('li[id="' + optionId + '"]');
        }

        return option;
    }

    function _toggleHighlightedOption(optionsList, selectedOptions, hiddenField) {
        let highlightedOption = optionsList.querySelector('.ah-multiselect-is-highlighted');
        _toggleOption(highlightedOption, selectedOptions, hiddenField);
    }

    // Flips the aria-selected value for the provided option
    function _toggleOption(option, selectedOptions, hiddenField) {
        if (option.getAttribute("aria-selected") === "true") {
            _deselectOption($(option)[0], selectedOptions, hiddenField);
        } else {
            _selectOption($(option)[0], selectedOptions, hiddenField);
        }
    }

    function _logDebug(message, object) {
        DevUtils.logDebug(message, 'A11yMultiSelect', object, _LOG_COLOR);
    }

})();
